import type { PayloadAction } from "../../../redux/types";

export const DRAGGING_ORDER = "dragging_order";
export const TRANSLATE_CURSOR = "translate_cursor";
export const IS_INSIDE_ORDER = "is_inside_order";
export const RESET_DRAGGED_ORDER = "reset_dragged_order";
export const SNAP_TO_SHIFT = "snap_to_shift";

const SHIFT = "shift";
const ORDER = "order";

export type ELEMENT_TYPES = typeof SHIFT | typeof ORDER;

export type TranslatePoint = {
    translate_x: number;
    translate_y: number;
};

export type DraggingOrder = {
    x0: number;
    y0: number;
    translate_x: number;
    translate_y: number;
    original_line_uuid: string;
    new_line_uuid: string;
    new_order_index: number;
    order_uuid: string;
    earliest_start: number;
    is_unscheduled_order: boolean;
    is_multiselect: boolean;
    production_version: string;
    line_uuid: string;
    order_index: number;
    line_index: number;
    override_earliest_start?: number;
    inside_order_uuid?: string;
};

export type IsInsideOrderAction = PayloadAction<typeof IS_INSIDE_ORDER, boolean>;
export type DraggingOrderAction = PayloadAction<typeof DRAGGING_ORDER, Partial<DraggingOrder> | null>;
export type TranslateCursorAction = PayloadAction<typeof TRANSLATE_CURSOR, TranslatePoint>;
export type ResetDraggedOrderAction = PayloadAction<typeof RESET_DRAGGED_ORDER, typeof undefined>;
export type SnapToShiftAction = PayloadAction<typeof SNAP_TO_SHIFT, boolean>;

export type ActionTypes =
    | DraggingOrderAction
    | IsInsideOrderAction
    | TranslateCursorAction
    | ResetDraggedOrderAction
    | SnapToShiftAction;

export type DraggableContextState = {
    translate_cursor: TranslatePoint | null;
    is_inside_order: boolean;
    dragging_order: DraggingOrder | null;
    snap_to_shift: boolean;
};

export const getInitialState = (): DraggableContextState => {
    return {
        translate_cursor: null,
        is_inside_order: false,
        dragging_order: null,
        snap_to_shift: true,
    };
};

export const getDraggingOrderTransform = (dragging_order: DraggingOrder, x_axis_only = false): string | undefined => {
    if (dragging_order.translate_y > 0) {
        const dx = dragging_order.translate_x - dragging_order.x0;
        const dy = x_axis_only ? 0 : dragging_order.translate_y - dragging_order.y0;
        return `translate(${dx}px, ${dy}px)`;
    }
};

export function draggableReducer(state: DraggableContextState, action: ActionTypes): DraggableContextState {
    let new_state: DraggableContextState = state;

    if (action.type === IS_INSIDE_ORDER) {
        new_state = {
            ...new_state,
            is_inside_order: action.data,
        };
    }
    if (action.type === DRAGGING_ORDER) {
        if (action.data) {
            new_state = {
                ...new_state,
                dragging_order: {
                    ...(new_state.dragging_order || {
                        x0: 0,
                        y0: 0,
                        translate_x: 0,
                        translate_y: 0,
                        original_line_uuid: "",
                        new_line_uuid: "",
                        new_order_index: 0,
                        order_uuid: "",
                        earliest_start: 0,
                        is_unscheduled_order: false,
                        is_multiselect: false,
                        production_version: "",
                        line_uuid: "",
                        order_index: 0,
                        line_index: 0,
                    }),
                    ...action.data,
                },
            };
        } else {
            new_state = {
                ...new_state,
                dragging_order: null,
            };
        }
    } else if (action.type === TRANSLATE_CURSOR) {
        new_state = {
            ...new_state,
            translate_cursor: action.data,
        };
    } else if (action.type === RESET_DRAGGED_ORDER) {
        new_state = {
            ...getInitialState(),
            snap_to_shift: new_state.snap_to_shift,
        };
    } else if (action.type === SNAP_TO_SHIFT) {
        new_state = {
            ...new_state,
            snap_to_shift: action.data,
        };
    }

    return new_state;
}
